import React, { useEffect, useRef, useState } from "react";
import css from "./.module.scss";
import { dispatchCtxChange } from "@/components/add-v2/ui";
import { Checkbox } from "@aqar-app/react-ui";

const rules = [];
const SpecialFields = [
  {
    name: "price_12_payments",
    denominator: 12,
    label: { ar: "أقبل الإيجار الشهري" },
  },
  {
    name: "price_4_payments",
    denominator: 4,
    label: { ar: "أقبل الإيجار الربع سنوي" },
    rules,
  },
  {
    name: "price_2_payments",
    denominator: 2,
    label: {
      ar: "أقبل الإيجار النصف سنوي",
    },
  },
];

// Helper function to get field label by name
const getFieldLabel = (fieldName) => {
  const field = SpecialFields.find((f) => f.name === fieldName);
  return field?.label?.ar || fieldName;
};

const formatter = new Intl.NumberFormat("en-SA", {});

function formatNumber(v = "") {
  const value = formatter.format(Number.isNaN(v) ? "" : v) || "";

  return value === "0" ? "" : value;
}

function formatAnnualTotal(v) {
  const numericValue = Number(v);

  return formatter.format(Number.isNaN(numericValue) ? 0 : numericValue);
}

export function PremiumsFieldComponent({ value, name, ctxRef } = {}) {
  const [error, setError] = useState(null);
  const [fieldsError, setFieldsError] = useState({});
  const rootRef = useRef(null);
  const ctx = ctxRef.current;

  const [values, setValues] = useState({
    price_2_payments: ctx.price_2_payments || null,
    price_4_payments: ctx.price_4_payments || null,
    price_12_payments: ctx.price_12_payments || null,
  });

  const [enabledFields, setEnabledFields] = useState({
    price_2_payments: ctx.price_2_payments_enabled !== false,
    price_4_payments: ctx.price_4_payments_enabled !== false,
    price_12_payments: ctx.price_12_payments_enabled !== false,
  });

  // Sync enabled fields to context
  useEffect(() => {
    SpecialFields.forEach(({ name: fieldName }) => {
      ctxRef.current[`${fieldName}_enabled`] = enabledFields[fieldName];
    });
  }, [enabledFields, ctxRef]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleValidate = (e) => {
      let validationError = null;

      for (const { name: fieldName } of SpecialFields) {
        const isEnabled = enabledFields[fieldName];
        const fieldValue = values[fieldName];

        if (isEnabled && (!fieldValue || fieldValue === "" || +fieldValue <= 0)) {
          validationError = `يجب إدخال قيمة صحيحة في ${getFieldLabel(fieldName)}`;
          setFieldsError((prevState) => ({
            ...prevState,
            [fieldName]: "الرجاء كتابة مبلغ الإيجار أو اغلاق الخيار",
          }));
          // break;
        }
      }

      setError(validationError || e.detail.error);

      e?.detail && (e.detail.error = validationError);
    };

    root.addEventListener("validate", handleValidate);

    return () => {
      root.removeEventListener("validate", handleValidate);
    };
  }, [enabledFields, values]);

  return (
    <div
      className={css.premiums + (error ? " " + css.hasError : "")}
      ref={rootRef}
      data-name={name}
    >
      {SpecialFields.map(({ name: fieldName, denominator, label }) => (
        <div key={fieldName}>
          <div className={css.reverseCheckbox}>
            <Checkbox
              name={fieldName}
              switch={true}
              checked={!!enabledFields[fieldName]}
              onChange={(e) => {
                const nextEnabled =
                  e?.target?.checked ?? !enabledFields[fieldName];

                setEnabledFields({
                  ...enabledFields,
                  [fieldName]: nextEnabled,
                });

                if (!nextEnabled) {
                  setValues({
                    ...values,
                    [fieldName]: null,
                  });
                  ctxRef.current[fieldName] = null;
                  dispatchCtxChange(e?.target, {
                    value: null,
                    name: fieldName,
                    ctx: ctxRef.current,
                  });
                }
              }}
            >
              <p>{label?.ar}</p>
            </Checkbox>
          </div>
          <div
            className={
              css.textField +
              (!enabledFields[fieldName] ? " " + css.readOnly : "") +
              (fieldsError[fieldName] ? " " + css.hasError : "")
            }
          >
            <input
              name={fieldName}
              value={formatNumber(values[fieldName])}
              onChange={(e) => {
                const currentValue = e.target.value.replaceAll(",", "");

                setValues({
                  ...values,
                  [fieldName]: currentValue,
                });

                ctxRef.current[name] = currentValue;
                dispatchCtxChange(e.target, {
                  value: +currentValue,
                  name: fieldName,
                  ctx: ctxRef.current,
                });
              }}
              inputMode="numeric"
              disabled={!enabledFields[fieldName]}
            />
          </div>
          {/*{!!enabledFields[fieldName] && (*/}
          {/*  <p>*/}
          {/*    إجمالي الإيجار السنوي:{" "}*/}
          {/*    {formatAnnualTotal(values[fieldName] * denominator)} ريال*/}
          {/*  </p>*/}
          {/*)}*/}
          {!!fieldsError[fieldName] && (
            <p className={css.error}>{fieldsError[fieldName]}</p>
          )}
          {enabledFields[fieldName] && !fieldsError[fieldName] && error && (
            <p className={css.error}>{error}</p>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={css.premiums + (error ? " " + css.hasError : "")}
      ref={rootRef}
    >
      <input
        readOnly
        hidden
        name={name}
        value={
          ctx.price_2_payments || ctx.price_4_payments || ctx.price_12_payments
        }
      />
      <table>
        <thead>
          <tr>
            <th>عدد الدفعات</th>
            <th>الإجمالي السنوي</th>
            <th>قيمة كل دفعة</th>
          </tr>
        </thead>
        <tbody>
          {SpecialFields.map(({ name: fieldName, denominator, label }) => (
            <tr key={fieldName}>
              <td>{label?.ar}</td>
              <td>
                <input
                  name={fieldName}
                  value={formatNumber(values[fieldName])}
                  onChange={(e) => {
                    const currentValue = e.target.value.replaceAll(",", "");

                    setValues({
                      ...values,
                      [fieldName]: currentValue,
                    });

                    ctxRef.current[name] = currentValue;
                    dispatchCtxChange(e.target, {
                      value: +currentValue,
                      name: fieldName,
                      ctx: ctxRef.current,
                    });
                  }}
                  inputMode="numeric"
                />
              </td>
              <td>
                <input
                  name={fieldName}
                  value={formatNumber(values[fieldName] / denominator)}
                  onChange={(e) => {
                    const currentValue = e.target.value.replaceAll(",", "");

                    setValues({
                      ...values,
                      [fieldName]: currentValue * denominator,
                    });
                    ctxRef.current[name] = currentValue;

                    dispatchCtxChange(e.target, {
                      value: currentValue * denominator,
                      name: fieldName,
                      ctx: ctxRef.current,
                    });
                  }}
                  inputMode="numeric"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={css.error}> {error}</p>
    </div>
  );
}
